import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { MapPin, ChefHat, ShoppingCart, Apple, Play, ArrowRight, Store, Clock, PiggyBank } from 'lucide-react';

// Fallback image URLs (used if env variables are missing in any deployment)
const FALLBACK_IMAGES = {
  LOGO: 'https://customer-assets.emergentagent.com/job_grocery-saver-4/artifacts/boaapnb1_image%20%281%29.png',
  HERO_SCREENSHOT: 'https://customer-assets.emergentagent.com/job_grocery-saver-4/artifacts/xk4wdzal_app1.JPG',
  STORES_SCREENSHOT: 'https://customer-assets.emergentagent.com/job_grocery-saver-4/artifacts/fv8gt965_app5.JPG',
  PROFILE_SCREENSHOT: 'https://customer-assets.emergentagent.com/job_grocery-saver-4/artifacts/4wj84v02_app3.JPG',
  RECIPE_DETAILS_SCREENSHOT: 'https://customer-assets.emergentagent.com/job_grocery-saver-4/artifacts/h0swgs35_app6.JPG',
  SALES_SCREENSHOT: 'https://customer-assets.emergentagent.com/job_grocery-saver-4/artifacts/tmd2be6u_app7.JPG',
  RECIPE_IDEAS_SCREENSHOT: 'https://customer-assets.emergentagent.com/job_grocery-saver-4/artifacts/70a24v7g_app8.JPG',
};

const LandingPage = ({ onGetStarted }) => {
  const appStoreUrl = 'https://apps.apple.com/ca/app/salesavor/id6756827243';
  const googlePlayUrl = 'https://play.google.com/store/apps/details?id=ca.salesavor.app';
  const openInNewTab = (url) => window.open(url, '_blank', 'noopener,noreferrer');

  // Image URLs with safe fallbacks so images always load even if env vars are missing
  const logoUrl = process.env.REACT_APP_LOGO_URL || FALLBACK_IMAGES.LOGO;
  const heroScreenshot = process.env.REACT_APP_HERO_SCREENSHOT || FALLBACK_IMAGES.HERO_SCREENSHOT;
  const storesScreenshot = process.env.REACT_APP_STORES_SCREENSHOT || FALLBACK_IMAGES.STORES_SCREENSHOT;
  const profileScreenshot = process.env.REACT_APP_PROFILE_SCREENSHOT || FALLBACK_IMAGES.PROFILE_SCREENSHOT;
  const recipeDetailsScreenshot = process.env.REACT_APP_RECIPE_DETAILS_SCREENSHOT || FALLBACK_IMAGES.RECIPE_DETAILS_SCREENSHOT;
  const salesScreenshot = process.env.REACT_APP_SALES_SCREENSHOT || FALLBACK_IMAGES.SALES_SCREENSHOT;
  const recipeIdeasScreenshot = process.env.REACT_APP_RECIPE_IDEAS_SCREENSHOT || FALLBACK_IMAGES.RECIPE_IDEAS_SCREENSHOT;

  // Marketing screenshots for the "See SaleSavor in action" showcase (served from /public/screenshots)
  const screenshots = [
    { src: '/screenshots/01-plan-meals', alt: 'SaleSavor: plan meals and save on groceries' },
    { src: '/screenshots/02-cook-from-pantry', alt: 'Cook from your pantry' },
    { src: '/screenshots/03-skip-what-you-avoid', alt: 'Skip ingredients you avoid, like onion, mushrooms and cilantro' },
    { src: '/screenshots/04-recipes-from-sales', alt: "Recipes from this week's sales" },
    { src: '/screenshots/05-plan-your-week', alt: 'Plan your week with recipe details' },
    { src: '/screenshots/06-one-list-every-ingredient', alt: 'One shopping list with every ingredient' },
    { src: '/screenshots/07-share-with-family', alt: 'Share your list with your family' },
    { src: '/screenshots/08-free-iphone-android', alt: 'Free on iPhone and Android' },
  ];
  const [lightbox, setLightbox] = useState(null);
  const galleryRef = useRef(null);

  // Gentle fade-and-rise reveal for each screenshot as it scrolls into view
  useEffect(() => {
    const root = galleryRef.current;
    if (!root) return;
    const items = root.querySelectorAll('.reveal-item');
    if (!items.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    items.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // Handler to swap to fallback if an image still fails to load at runtime
  const handleImgError = (fallbackUrl) => (e) => {
    if (e.target.src !== fallbackUrl) {
      e.target.src = fallbackUrl;
    }
  };

  const scrollToSales = () => {
    if (onGetStarted) {
      onGetStarted();
    } else {
      window.location.href = '/sales';
    }
  };

  return (
    <div className="min-h-screen" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      {/* Hero Section */}
      <section className="min-h-screen flex items-center px-4 sm:px-6 lg:px-8" style={{ background: 'linear-gradient(135deg, #2C5F2D 0%, #0E192A 100%)' }}>
        <div className="max-w-7xl mx-auto w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Hero Content */}
            <div className="text-center lg:text-left">
              {/* App Icon + Brand */}
              <div className="flex items-center justify-center lg:justify-start gap-4 md:gap-5 mb-6 md:mb-8">
                <img 
                  src={logoUrl} 
                  alt="SaleSavor App Icon" 
                  className="h-16 w-16 md:h-[88px] md:w-[88px] rounded-2xl shadow-lg"
                  onError={handleImgError(FALLBACK_IMAGES.LOGO)}
                />
                <div>
                  <h2 className="text-2xl md:text-[36px] md:leading-tight font-bold" style={{ color: '#FFFFFF' }}>SaleSavor</h2>
                  <p className="text-sm md:text-lg" style={{ color: 'rgba(255,255,255,0.85)' }}>Save money, eat better</p>
                </div>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6" style={{ color: '#FFFFFF', lineHeight: '1.2' }}>
                <span style={{ color: '#B5F2A3' }}>Plan meals.</span> Save on groceries.
              </h1>
              <p className="text-xl md:text-2xl mb-8 leading-relaxed" style={{ color: 'rgba(255,255,255,0.85)' }}>
                Recipes from what&apos;s in your pantry and this week&apos;s sales, one shopping list for the week.
              </p>
              
              {/* App Store Buttons */}
              <div className="flex flex-wrap gap-4 items-center justify-center lg:justify-start mb-6">
                <a
                  href={appStoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Download SaleSavor on the App Store"
                  className="inline-block rounded-lg transform hover:scale-105 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
                >
                  <img src="/badges/appstore-white.svg" alt="Download on the App Store" style={{ height: '54px', width: 'auto', display: 'block' }} />
                </a>
                <a
                  href={googlePlayUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Get SaleSavor on Google Play"
                  className="inline-block rounded-lg transform hover:scale-105 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
                >
                  <img src="/badges/googleplay.png" alt="Get it on Google Play" style={{ height: '76px', width: 'auto', display: 'block' }} />
                </a>
              </div>
            </div>
            
            {/* Hero Image - App Screenshots */}
            <div className="flex justify-center lg:justify-end">
              <div className="relative">
                {/* Main hero screenshot */}
                <img 
                  src={heroScreenshot} 
                  alt="SaleSavor Mobile App Welcome Screen" 
                  className="w-72 h-auto rounded-3xl shadow-2xl border-4 border-white"
                  style={{ borderRadius: '24px' }}
                  onError={handleImgError(FALLBACK_IMAGES.HERO_SCREENSHOT)}
                />
                
                {/* Floating secondary screenshots */}
                <img 
                  src={storesScreenshot}
                  alt="Shopping List Screen" 
                  className="absolute -right-8 top-16 w-48 h-auto rounded-2xl shadow-xl border-2 border-white opacity-90"
                  style={{ borderRadius: '16px', transform: 'rotate(8deg)' }}
                  onError={handleImgError(FALLBACK_IMAGES.STORES_SCREENSHOT)}
                />
                
                <img 
                  src={recipeDetailsScreenshot}
                  alt="Recipe Details" 
                  className="absolute -left-6 bottom-8 w-44 h-auto rounded-2xl shadow-xl border-2 border-white opacity-85"
                  style={{ borderRadius: '16px', transform: 'rotate(-5deg)' }}
                  onError={handleImgError(FALLBACK_IMAGES.RECIPE_DETAILS_SCREENSHOT)}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#ffffff' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#2C5F2D' }}>
              Smart Grocery Shopping Made Simple
            </h2>
            <p className="text-xl" style={{ color: '#1A1A1A' }}>
              Everything you need to save money and eat well
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <Card className="text-center p-8 h-full shadow-lg hover:shadow-xl transition-shadow" style={{ borderRadius: '12px', backgroundColor: '#E8F5E9', border: 'none' }}>
              <CardContent className="p-0">
                <div className="flex justify-center mb-6">
                  <div className="w-20 h-20 rounded-full flex items-center justify-center" style={{ backgroundColor: '#FFFFFF' }}>
                    <Store className="w-10 h-10" style={{ color: '#2c5f2d' }} />
                  </div>
                </div>
                <h3 className="text-2xl font-bold mb-4" style={{ color: '#1a1a1a' }}>
                  Recipes From This Week&apos;s Sales
                </h3>
                <p className="text-lg leading-relaxed" style={{ color: '#1A1A1A' }}>
                  Find weekly sales at grocery stores near you and get recipes built around them.
                </p>
              </CardContent>
            </Card>
            
            {/* Feature 2 */}
            <Card className="text-center p-8 h-full shadow-lg hover:shadow-xl transition-shadow" style={{ borderRadius: '12px', backgroundColor: '#E8F5E9', border: 'none' }}>
              <CardContent className="p-0">
                <div className="flex justify-center mb-6">
                  <div className="w-20 h-20 rounded-full flex items-center justify-center" style={{ backgroundColor: '#FFFFFF' }}>
                    <ChefHat className="w-10 h-10" style={{ color: '#2c5f2d' }} />
                  </div>
                </div>
                <h3 className="text-2xl font-bold mb-4" style={{ color: '#1a1a1a' }}>
                  Cook From Your Pantry
                </h3>
                <p className="text-lg leading-relaxed" style={{ color: '#1A1A1A' }}>
                  Use what you already have, and skip the ingredients your family avoids.
                </p>
              </CardContent>
            </Card>
            
            {/* Feature 3 */}
            <Card className="text-center p-8 h-full shadow-lg hover:shadow-xl transition-shadow" style={{ borderRadius: '12px', backgroundColor: '#E8F5E9', border: 'none' }}>
              <CardContent className="p-0">
                <div className="flex justify-center mb-6">
                  <div className="w-20 h-20 rounded-full flex items-center justify-center" style={{ backgroundColor: '#FFFFFF' }}>
                    <PiggyBank className="w-10 h-10" style={{ color: '#2c5f2d' }} />
                  </div>
                </div>
                <h3 className="text-2xl font-bold mb-4" style={{ color: '#1a1a1a' }}>
                  One List for the Week
                </h3>
                <p className="text-lg leading-relaxed" style={{ color: '#1A1A1A' }}>
                  Combine your recipes into one shopping list and share it with your family.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#E8F5E9' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#2C5F2D' }}>
              How It Works
            </h2>
            <p className="text-xl" style={{ color: '#1A1A1A' }}>
              Three simple steps to start saving
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-12">
            {/* Step 1 */}
            <div className="text-center">
              <img 
                src={profileScreenshot}
                alt="Set Your Preferences" 
                className="w-48 h-auto mx-auto mb-6 rounded-xl shadow-lg"
                style={{ borderRadius: '16px' }}
                onError={handleImgError(FALLBACK_IMAGES.PROFILE_SCREENSHOT)}
              />
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-lg font-bold text-white mx-auto mb-4" 
                   style={{ backgroundColor: '#2c5f2d' }}>
                1
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#1a1a1a' }}>
                Set Your Preferences
              </h3>
              <p className="text-lg leading-relaxed" style={{ color: '#1A1A1A' }}>
                Tell us your family size, dietary needs, and health concerns
              </p>
            </div>
            
            {/* Step 2 */}
            <div className="text-center">
              <img 
                src={salesScreenshot}
                alt="See What's On Sale" 
                className="w-48 h-auto mx-auto mb-6 rounded-xl shadow-lg"
                style={{ borderRadius: '16px' }}
                onError={handleImgError(FALLBACK_IMAGES.SALES_SCREENSHOT)}
              />
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-lg font-bold text-white mx-auto mb-4" 
                   style={{ backgroundColor: '#2c5f2d' }}>
                2
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#1a1a1a' }}>
                See What's On Sale
              </h3>
              <p className="text-lg leading-relaxed" style={{ color: '#1A1A1A' }}>
                Browse weekly flyers from nearby grocery stores
              </p>
            </div>
            
            {/* Step 3 */}
            <div className="text-center">
              <img 
                src={recipeIdeasScreenshot}
                alt="Get Recipe Ideas" 
                className="w-48 h-auto mx-auto mb-6 rounded-xl shadow-lg"
                style={{ borderRadius: '16px' }}
                onError={handleImgError(FALLBACK_IMAGES.RECIPE_IDEAS_SCREENSHOT)}
              />
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-lg font-bold text-white mx-auto mb-4" 
                   style={{ backgroundColor: '#2c5f2d' }}>
                3
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#1a1a1a' }}>
                Get Recipe Ideas
              </h3>
              <p className="text-lg leading-relaxed" style={{ color: '#1A1A1A' }}>
                Get recipe ideas based on sales and your profile
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* See SaleSavor in action - screenshots showcase */}
      <section className="py-24 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#FFFFFF' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#2C5F2D' }}>
              See SaleSavor in action
            </h2>
            <p className="text-xl" style={{ color: '#1A1A1A' }}>
              Plan meals, skip what you avoid, and shop one list for the week.
            </p>
          </div>

          <div ref={galleryRef} className="flex md:grid md:grid-cols-4 gap-4 overflow-x-auto md:overflow-visible snap-x snap-mandatory md:snap-none pb-4 md:pb-0 -mx-4 px-4 md:mx-0 md:px-0">
            {screenshots.map((s, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setLightbox(s)}
                aria-label={`View larger: ${s.alt}`}
                style={{ transitionDelay: `${(i % 4) * 90}ms` }}
                className="reveal-item snap-start shrink-0 basis-[72%] md:basis-auto md:w-full p-0 bg-transparent border-0 cursor-pointer rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2C5F2D] focus-visible:ring-offset-2"
              >
                <picture>
                  <source srcSet={`${s.src}.webp`} type="image/webp" />
                  <img
                    src={`${s.src}.png`}
                    alt={s.alt}
                    loading="lazy"
                    className="w-full h-auto block rounded-2xl shadow-lg"
                  />
                </picture>
              </button>
            ))}
          </div>

          {/* Store badges */}
          <div className="mt-12 text-center">
            <p className="text-lg font-semibold mb-4" style={{ color: '#2C5F2D' }}>Download free today</p>
            <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6">
              <a
                href={appStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Download SaleSavor on the App Store"
                className="inline-block rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2C5F2D] focus-visible:ring-offset-2"
              >
                <img src="/badges/appstore.svg" alt="Download on the App Store" loading="lazy" style={{ height: '52px', width: 'auto', display: 'block' }} />
              </a>
              <a
                href={googlePlayUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Get SaleSavor on Google Play"
                className="inline-block rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2C5F2D] focus-visible:ring-offset-2"
              >
                <img src="/badges/googleplay.png" alt="Get it on Google Play" loading="lazy" style={{ height: '74px', width: 'auto', display: 'block' }} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox for screenshots */}
      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.8)' }}
        >
          <button
            type="button"
            onClick={() => setLightbox(null)}
            aria-label="Close"
            className="absolute top-4 right-6 text-white text-4xl leading-none bg-transparent border-0 cursor-pointer"
          >
            &times;
          </button>
          <picture onClick={(e) => e.stopPropagation()}>
            <source srcSet={`${lightbox.src}.webp`} type="image/webp" />
            <img
              src={`${lightbox.src}.png`}
              alt={lightbox.alt}
              className="max-h-[90vh] w-auto rounded-2xl"
              style={{ boxShadow: '0 25px 50px -12px rgba(0,0,0,0.6)' }}
            />
          </picture>
        </div>
      )}

      {/* Download CTA Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#2c5f2d' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#ffffff' }}>
            Ready to Start Saving?
          </h2>
          <p className="text-xl mb-8" style={{ color: '#ffffff', opacity: 0.9 }}>
            Join families across Canada and the US planning smarter meals.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
            <Button 
              className="flex items-center gap-3 px-8 py-6 text-lg font-bold rounded-xl shadow-lg transform hover:scale-105 transition-all cursor-pointer"
              style={{ backgroundColor: '#ffffff', color: '#2c5f2d' }}
              onClick={() => openInNewTab(appStoreUrl)}
            >
              <Apple className="h-6 w-6" />
              Available on iOS
            </Button>
            <Button 
              className="flex items-center gap-3 px-8 py-6 text-lg font-bold rounded-xl shadow-lg transform hover:scale-105 transition-all cursor-pointer"
              style={{ backgroundColor: '#ffffff', color: '#2c5f2d' }}
              onClick={() => openInNewTab(googlePlayUrl)}
            >
              <Play className="h-6 w-6" />
              Get it on Google Play
            </Button>
          </div>
          
          <p className="text-sm" style={{ color: '#ffffff', opacity: 0.8 }}>
            Available on iOS and Android
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#0E192A' }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            {/* Left Column */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src={logoUrl} 
                  alt="SaleSavor App Icon" 
                  className="h-10 w-10 rounded-xl"
                  onError={handleImgError(FALLBACK_IMAGES.LOGO)}
                />
                <span className="text-xl font-bold" style={{ color: '#ffffff' }}>
                  SaleSavor
                </span>
              </div>
              <p style={{ color: '#ffffff', opacity: 0.8 }}>
                © 2026 SaleSavor. All rights reserved.
              </p>
            </div>
            
            {/* Center Column */}
            <div>
              <div className="space-y-2">
                <a href="/privacy-policy" className="block text-white/80 hover:text-[#B5F2A3] transition-colors hover:underline">
                  Privacy Policy
                </a>
                <a href="/terms-of-service" className="block text-white/80 hover:text-[#B5F2A3] transition-colors hover:underline">
                  Terms of Service
                </a>
                <a href="/delete-account" className="block text-white/80 hover:text-[#B5F2A3] transition-colors hover:underline">
                  Delete Account
                </a>
                <a href="/support" className="block text-white/80 hover:text-[#B5F2A3] transition-colors hover:underline">
                  Support
                </a>
                <a href="#" className="termly-display-preferences block text-white/80 hover:text-[#B5F2A3] transition-colors hover:underline">
                  Cookie Preferences
                </a>
              </div>
            </div>
            
            {/* Right Column */}
            <div>
              <div className="space-y-2" style={{ color: '#ffffff', opacity: 0.8 }}>
                <p>Contact: info@salesavor.ca</p>
                <p>Kingston, Ontario, Canada</p>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;